import { SORT_ORDER } from '@/constants'
import apiClient from '@/lib/apiClient'
import type { SortingState } from '@tanstack/react-table'
import { endpoints } from './endPoint'
import type { EventTicket } from '@/pages/Dashboard/AddEditEvent'
import type { EventSponsorShipPayloadType } from '@/schemas/eventForm.schema'

export interface EventGuestUser {
  id?: string
  name: string
  designation: string
  profile: string
}

export interface EventHighlight {
  id?: string
  title: string
  description: string
  startDateTime?: string | null
  endDateTime?: string | null
  image?: string | null
}

export interface createEventPayload {
  name: string
  categoryId: string
  description: string
  images: Array<{
    id?: string
    key: string
    index?: number
    url?: string
  }>
  document?: string
  eventInfoDocument?: string
  startDateTime: string
  endDateTime: string
  timeZone: string
  registrationDeadline: string
  location: string
  zoomLink: string
  venueName: string
  venueAddress: string
  venueCity: string
  venueState: string
  type: string
  tickets: Array<EventTicket>
  maxAttendeesLimit?: number
  eventAdmins?: string[]
  organizerWebsite?: string
  specialInstructions: string | undefined
  guests: Array<EventGuestUser>
  venueLatitude: number | undefined
  venueLongitude: number | undefined
  venueCountry: string
  eventTags?: string[]
  sponsorshipPackages: EventSponsorShipPayloadType[]
  activityHighlights?: EventHighlight[]
  status?: string
}

export const fetchEvents = async (params: {
  limit?: number
  page?: number
  search?: string
  sort?: SortingState
  status?: string[]
  startDate?: string
  endDate?: string
  categories?: string[]
  fetchNamesOnly?: boolean
}) => {
  try {
    const queryParams = new URLSearchParams()
    if (params.limit) queryParams.append('limit', params?.limit?.toString())
    if (params.page) queryParams.append('page', params?.page?.toString())
    if (params.search) queryParams.append('search', params?.search)
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append(
        'order',
        params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
      )
    }
    if (params?.sort && params?.sort?.length > 0) {
      queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
    }
    if (params.status) {
      params?.status?.forEach(s => {
        queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
      })
    }
    if (params.categories) {
      params.categories?.forEach(category => {
        queryParams.append('categories', category)
      })
    }
    if (params.startDate) {
      queryParams.append('startDate', params.startDate)
    }
    if (params.endDate) {
      queryParams.append('endDate', params.endDate)
    }
    if (params.fetchNamesOnly) {
      queryParams.append('fetchNamesOnly', 'true')
    }

    const response = await apiClient.get(
      `${endpoints.superAdmin.event}?${queryParams.toString()}`
    )
    return response.data
  } catch (error) {
    console.error('Error fetching events:', error)
    throw error
  }
}

export const getCategory = async () => {
  try {
    const response = await apiClient.get(endpoints.event.category)
    return response.data
  } catch (error) {
    console.error('Error fetching categories:', error)
    throw error
  }
}

export const createEvent = async (payload: createEventPayload) => {
  try {
    const response = await apiClient.post(endpoints.event.event, payload)
    return response.data
  } catch (error) {
    console.error('Error while creating events:', error)
    throw error
  }
}

export const getEventById = async (id: string) => {
  try {
    const response = await apiClient.get(endpoints.event.getEventById(id))
    return response.data
  } catch (error) {
    console.error('Error fetching event:', error)
    throw error
  }
}

export const updateEvent = async ({
  id,
  payload,
}: {
  id: string
  payload: createEventPayload
}) => {
  try {
    const response = await apiClient.put(endpoints.event.editEvent(id), payload)
    return response.data
  } catch (error) {
    console.error('Error updating event:', error)
    throw error
  }
}

export const updateEventStatus = async ({
  id,
  payload,
}: {
  id: string
  payload: {
    status: string
  }
}) => {
  try {
    const response = await apiClient.put(
      endpoints.event.updateEventStatus(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error updating event status:', error)
    throw error
  }
}

export interface GetEventExportParams {
  search?: string
  categories?: string[]
  status?: string[]
  startDate?: string
  endDate?: string
  sort: SortingState
}

export const getEventExport = async (
  params: GetEventExportParams
): Promise<Blob> => {
  const queryParams = new URLSearchParams()

  if (params.search) {
    queryParams.append('search', params.search)
  }
  if (params.categories) {
    params.categories?.forEach(category => {
      queryParams.append('categories', category)
    })
  }
  if (params.status) {
    params?.status?.forEach(s => {
      queryParams.append('statuses', s.split(' ').join('_').toLowerCase())
    })
  }
  if (params.startDate) {
    queryParams.append('startDate', params.startDate)
  }
  if (params.endDate) {
    queryParams.append('endDate', params.endDate)
  }
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append(
      'order',
      params?.sort?.[0].desc ? SORT_ORDER.DESC : SORT_ORDER.ASC
    )
  }
  if (params?.sort && params?.sort?.length > 0) {
    queryParams.append('orderOn', params?.sort?.[0].id || 'createdAt')
  }

  const response = await apiClient.get(endpoints.event.getExportEvent, {
    params: queryParams,
    responseType: 'blob',
  })

  return response.data
}

export const getPromoCodeUsageExport = async (eventId: string) => {
  const response = await apiClient.get(
    endpoints.event.getExportPromoCodeUsage,
    {
      params: { eventId },
      responseType: 'blob',
    }
  )

  return response.data
}

export const getEventStatistics = async (params: {
  month?: string
  year?: string
}) => {
  const queryParams = new URLSearchParams()
  if (params?.month) queryParams.append('month', params?.month?.toString())
  if (params?.year) queryParams.append('year', params?.year?.toString())

  const response = await apiClient.get(
    `${endpoints.event.getEventStatistics}?${queryParams.toString()}`
  )
  return response.data
}

export const updateEventAcknowledge = async ({
  id,
  payload,
}: {
  id: string
  payload: {
    status: string
  }
}) => {
  try {
    const response = await apiClient.put(
      endpoints.event.updateEventAcknowledge(id),
      payload
    )
    return response.data
  } catch (error) {
    console.error('Error updating event acknowledgment:', error)
    throw error
  }
}

export const getTag = async () => {
  try {
    const response = await apiClient.get(endpoints.event.tag)
    return response.data
  } catch (error) {
    console.error('Error fetching tags:', error)
    throw error
  }
}

export const initiateRefund = async (
  transactionId: string,
  refundPercentage: number
) => {
  try {
    const response = await apiClient.post(
      endpoints.event.initiateRefund(transactionId),
      { refundPercentage }
    )
    return response.data
  } catch (error) {
    console.error('Error initiating refund:', error)
    throw error
  }
}

export const updatePlatformFees = async (platformFees: number) => {
  try {
    const response = await apiClient.put(endpoints.event.updatePlatformFees, {
      feesPercentage: platformFees,
    })
    return response.data
  } catch (error) {
    console.error('Error updating platform fees:', error)
    throw error
  }
}

export const getEventDates = async (id: string) => {
  try {
    const response = await apiClient.get(endpoints.event.getEventDates(id))
    return response.data
  } catch (error) {
    console.error('Error fetching event dates:', error)
    throw error
  }
}

export const getPaymentStatus = async (transactionId: string) => {
  try {
    const response = await apiClient.get(
      endpoints.event.getPaymentStatus(transactionId)
    )
    return response.data
  } catch (error) {
    console.error('Error fetching payment status:', error)
    throw error
  }
}

interface UpdatePaymentStatusParams {
  transactionId: string
  reason?: string
}

export const updatePaymentStatus = async ({
  transactionId,
  reason,
}: UpdatePaymentStatusParams) => {
  try {
    const response = await apiClient.put(
      endpoints.event.updatePaymentStatus(transactionId),
      reason ? { reason } : undefined
    )

    return response.data
  } catch (error) {
    console.error('Error updating payment status:', error)
    throw error
  }
}
